import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, X, Filter, Clock, FileText, AlertTriangle, 
  ClipboardList, GraduationCap, Users, Building2,
  ChevronRight, Loader2, Command
} from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEARCH_CATEGORIES = [
  { id: 'all', label: 'All', icon: Search },
  { id: 'incidents', label: 'Incidents', icon: AlertTriangle },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'training', label: 'Training', icon: GraduationCap },
  { id: 'audits', label: 'Audits', icon: ClipboardList },
  { id: 'employees', label: 'Employees', icon: Users }
];

const AdvancedSearch = ({ isOpen, onClose, isDarkMode = true }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const inputRef = useRef(null);
  
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [results, setResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('wsafe-recent-searches');
    if (saved) {
      setRecentSearches(JSON.parse(saved).slice(0, 5));
    }
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && results[selectedIndex]) {
        handleResultClick(results[selectedIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose]);

  // Debounced search
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      // Search across multiple endpoints based on category
      const searchPromises = [];
      
      if (category === 'all' || category === 'incidents') {
        searchPromises.push(
          axios.get(`${API_URL}/api/incidents`, {
            params: { search: searchQuery, limit: 5 },
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => res.data.incidents?.map(i => ({
            ...i,
            type: 'incident',
            title: i.title || `Incident ${i.incident_number}`,
            subtitle: i.description?.substring(0, 100) || i.location,
            path: `/incidents/${i.id}`
          })) || []).catch(() => [])
        );
      }

      if (category === 'all' || category === 'documents') {
        searchPromises.push(
          axios.get(`${API_URL}/api/documents`, {
            params: { search: searchQuery, limit: 5 },
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => res.data.documents?.map(d => ({
            ...d,
            type: 'document',
            title: d.title || d.name,
            subtitle: d.category || 'Document',
            path: `/documents/${d.id}`
          })) || []).catch(() => [])
        );
      }

      if (category === 'all' || category === 'training') {
        searchPromises.push(
          axios.get(`${API_URL}/api/training/courses`, {
            params: { search: searchQuery, limit: 5 },
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => res.data.courses?.map(t => ({
            ...t,
            type: 'training',
            title: t.title || t.name,
            subtitle: `${t.duration || 0} min • ${t.category || 'Training'}`,
            path: `/training/${t.id}`
          })) || []).catch(() => [])
        );
      }

      const allResults = await Promise.all(searchPromises);
      const combinedResults = allResults.flat().slice(0, 10);
      
      // Add mock results if no real results (for demo)
      if (combinedResults.length === 0) {
        setResults(getMockResults(searchQuery, category));
      } else {
        setResults(combinedResults);
      }
      
      setSelectedIndex(0);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  }, [category]);

  // Mock results for demo
  const getMockResults = (searchQuery, cat) => {
    const lowerQuery = searchQuery.toLowerCase();
    const mockData = [
      { type: 'incident', title: 'Fall from ladder - Building A', subtitle: 'Dec 15, 2025 • Medium severity', path: '/incidents' },
      { type: 'incident', title: 'Chemical spill in warehouse', subtitle: 'Dec 10, 2025 • High severity', path: '/incidents' },
      { type: 'document', title: 'Emergency Response Plan', subtitle: 'Safety Documentation', path: '/documentation' },
      { type: 'document', title: 'PPE Guidelines 2025', subtitle: 'Safety Documentation', path: '/documentation' },
      { type: 'training', title: 'OSHA 10-Hour Safety Training', subtitle: '10 hours • Compliance', path: '/training' },
      { type: 'training', title: 'First Aid Certification', subtitle: '4 hours • Emergency', path: '/training' },
      { type: 'audit', title: 'Q4 Safety Audit Report', subtitle: 'Completed Dec 20, 2025', path: '/audits' },
      { type: 'employee', title: 'Safety Team Members', subtitle: '12 members', path: '/team' }
    ];

    return mockData
      .filter(item => {
        if (cat !== 'all' && item.type !== cat.replace('s', '').replace('ie', 'y')) return false;
        return item.title.toLowerCase().includes(lowerQuery) || 
               item.subtitle.toLowerCase().includes(lowerQuery);
      })
      .slice(0, 6);
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, performSearch]);

  const handleResultClick = (result) => {
    // Save to recent searches
    const newRecent = [
      { query, timestamp: Date.now() },
      ...recentSearches.filter(r => r.query !== query)
    ].slice(0, 5);
    setRecentSearches(newRecent);
    localStorage.setItem('wsafe-recent-searches', JSON.stringify(newRecent));

    navigate(result.path);
    onClose();
    setQuery('');
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'incident': return AlertTriangle;
      case 'document': return FileText;
      case 'training': return GraduationCap;
      case 'audit': return ClipboardList;
      case 'employee': return Users;
      default: return FileText;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'incident': return 'text-red-500 bg-red-500/10';
      case 'document': return 'text-blue-500 bg-blue-500/10';
      case 'training': return 'text-green-500 bg-green-500/10';
      case 'audit': return 'text-purple-500 bg-purple-500/10';
      case 'employee': return 'text-amber-500 bg-amber-500/10';
      default: return 'text-slate-500 bg-slate-500/10';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Search Modal */}
      <div className={`relative w-full max-w-2xl mx-4 rounded-2xl shadow-2xl overflow-hidden ${
        isDarkMode ? 'bg-slate-800' : 'bg-white'
      }`}>
        {/* Search Input */}
        <div className={`flex items-center gap-3 p-4 border-b ${
          isDarkMode ? 'border-slate-700' : 'border-slate-200'
        }`}>
          <Search className={`w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Search incidents, documents, training..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            className={`flex-1 border-0 focus-visible:ring-0 text-lg ${
              isDarkMode ? 'bg-transparent text-white placeholder:text-slate-500' : 'bg-transparent'
            }`}
          />
          {loading && <Loader2 className="w-5 h-5 animate-spin text-blue-500" />}
          <button
            onClick={onClose}
            className={`p-1.5 rounded-lg transition-colors ${
              isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Category Filters */}
        <div className={`flex gap-2 p-3 border-b overflow-x-auto ${
          isDarkMode ? 'border-slate-700' : 'border-slate-200'
        }`}>
          {SEARCH_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                category === cat.id
                  ? 'bg-blue-600 text-white'
                  : isDarkMode
                    ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <cat.icon className="w-4 h-4" />
              {cat.label}
            </button>
          ))}
        </div>

        {/* Results */}
        <div className="max-h-[50vh] overflow-y-auto">
          {query.length >= 2 && results.length > 0 && (
            <div className="p-2">
              <p className={`px-3 py-2 text-xs font-medium uppercase ${
                isDarkMode ? 'text-slate-500' : 'text-slate-400'
              }`}>
                Results
              </p>
              {results.map((result, index) => {
                const Icon = getTypeIcon(result.type);
                const colorClass = getTypeColor(result.type);
                return (
                  <button
                    key={index}
                    onClick={() => handleResultClick(result)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                      index === selectedIndex
                        ? isDarkMode ? 'bg-slate-700' : 'bg-slate-100'
                        : isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorClass}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {result.title}
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {result.subtitle}
                      </p>
                    </div>
                    <ChevronRight className={`w-5 h-5 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                  </button>
                );
              })}
            </div>
          )}

          {/* Empty State */}
          {query.length >= 2 && results.length === 0 && !loading && (
            <div className="p-8 text-center">
              <Search className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`} />
              <p className={`font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                No results found for "{query}"
              </p>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Try adjusting your search or filters
              </p>
            </div>
          )}

          {/* Recent Searches */}
          {query.length < 2 && recentSearches.length > 0 && (
            <div className="p-2">
              <p className={`px-3 py-2 text-xs font-medium uppercase ${
                isDarkMode ? 'text-slate-500' : 'text-slate-400'
              }`}>
                Recent Searches
              </p>
              {recentSearches.map((recent, index) => (
                <button
                  key={index}
                  onClick={() => setQuery(recent.query)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                    isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'
                  }`}
                >
                  <Clock className={`w-4 h-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                  <span className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>
                    {recent.query}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Quick Links */}
          {query.length < 2 && recentSearches.length === 0 && (
            <div className="p-4">
              <p className={`px-3 py-2 text-xs font-medium uppercase ${
                isDarkMode ? 'text-slate-500' : 'text-slate-400'
              }`}>
                Quick Links
              </p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {[
                  { label: 'Report Incident', path: '/incidents/new', icon: AlertTriangle },
                  { label: 'View Documents', path: '/documentation', icon: FileText },
                  { label: 'Training Center', path: '/training', icon: GraduationCap },
                  { label: 'Safety Dashboard', path: '/dashboard', icon: Building2 }
                ].map((link, index) => (
                  <button
                    key={index}
                    onClick={() => { navigate(link.path); onClose(); }}
                    className={`flex items-center gap-2 p-3 rounded-xl transition-colors ${
                      isDarkMode 
                        ? 'bg-slate-700/50 hover:bg-slate-700 text-slate-300' 
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                    }`}
                  >
                    <link.icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{link.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between p-3 border-t text-xs ${
          isDarkMode ? 'border-slate-700 text-slate-500' : 'border-slate-200 text-slate-400'
        }`}>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>↑↓</kbd>
              Navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>↵</kbd>
              Select
            </span>
            <span className="flex items-center gap-1">
              <kbd className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>esc</kbd>
              Close
            </span>
          </div>
          <span className="flex items-center gap-1">
            <Command className="w-3 h-3" />K to search
          </span>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSearch;
